import type { AttackResult, AttackType } from '../types/attack';
import { formatEnumLabel } from './format';

export function hasAnyGain(result: AttackResult): boolean {
  if (result.landCaptured > 0) return true;
  if (result.prisonersCaptured > 0) return true;
  if ((result.landDestroyed ?? 0) > 0) return true;
  if (Object.values(result.resourcesStolen ?? {}).some((v) => v > 0)) return true;
  if (Object.values(result.scienceStolen ?? {}).some((v) => v > 0)) return true;
  if (Object.values(result.unitsMassacred ?? {}).some((v) => v > 0)) return true;
  return false;
}

export function formatGainLabel(
  category: 'resources' | 'science' | 'massacre',
  key: string
): string {
  switch (category) {
    case 'resources':
      return `${formatEnumLabel(key)} Stolen`;
    case 'science':
      return `${formatEnumLabel(key)} Books Stolen`;
    case 'massacre':
      return `${formatEnumLabel(key)}s Massacred`;
  }
}

// ─── SF-180 Component 4: combat report narrative ────────────────────────────

export type MarginBucket = 'narrow' | 'moderate' | 'decisive';

/**
 * Pure margin-bucket derivation, guarded against `defenderDefense === 0` — a real, reachable
 * value (not hypothetical), not just a division-by-zero formality. Only modulates narrative
 * INTENSITY; win/loss direction always comes from `outcome` directly, never re-derived from this
 * ratio (Decision 11 — the same rule this spec's other fixes apply throughout).
 */
export function computeMarginBucket(attackerOffense: number, defenderDefense: number): MarginBucket {
  if (defenderDefense <= 0) return 'decisive';
  const ratio = attackerOffense / defenderDefense;
  const margin = Math.abs(ratio - 1);
  if (margin < 0.1) return 'narrow';
  if (margin < 0.5) return 'moderate';
  return 'decisive';
}

const VICTORY_BUCKET_OPENERS: Record<MarginBucket, string> = {
  narrow: 'Your forces narrowly overcame',
  moderate: 'Your forces overpowered',
  decisive: 'Your forces overwhelmed',
};

const DEFEAT_BUCKET_OPENERS: Record<MarginBucket, string> = {
  narrow: 'Your forces were narrowly turned back by',
  moderate: 'Your forces were repelled by',
  decisive: 'Your forces were routed by',
};

// Allowed-inputs contract (Decision 11): copy varies only by outcome/attackType/margin bucket —
// no flavor text implying unobserved state (buildings held, composition, spells cast) the report
// never actually returned. Deliberately avoids the literal words "building"/"composition"/"spell".
const VICTORY_ATTACK_TYPE_ACTION: Record<AttackType, string> = {
  TRADITIONAL_MARCH: "and seized enemy territory",
  PLUNDER: "and made off with plundered resources",
  LEARN: "and stole away with enemy science",
  CONQUEST: "and claimed the province outright",
  MASSACRE: "and cut down the defending population",
  RAZE: "and left enemy holdings in ruins",
  AMBUSH: "and turned the tables on the ambush",
};

const DEFEAT_ATTACK_TYPE_ACTION: Record<AttackType, string> = {
  TRADITIONAL_MARCH: "before they could take any ground",
  PLUNDER: "and returned home empty-handed",
  LEARN: "before they could steal any science",
  CONQUEST: "and the province remains in enemy hands",
  MASSACRE: "before the slaughter could begin",
  RAZE: "before they could raze a single acre",
  AMBUSH: "and the ambush attempt collapsed",
};

/**
 * Builds the 1-2 sentence combat report lead paragraph shown between the outcome header and the
 * Combat Summary block.
 *
 * AMBUSH carve-out: `defenderDefense` for an AMBUSH attack is the restricted
 * `computeAmbushDefense` chain, not home defense — narrative copy must not phrase it as "their
 * home defenses".
 */
export function buildCombatNarrative(result: AttackResult): string {
  const isVictory = result.outcome === 'VICTORY';
  const bucket = computeMarginBucket(result.attackerOffense, result.defenderDefense);
  const opener = isVictory ? VICTORY_BUCKET_OPENERS[bucket] : DEFEAT_BUCKET_OPENERS[bucket];
  const action = isVictory ? VICTORY_ATTACK_TYPE_ACTION[result.attackType] : DEFEAT_ATTACK_TYPE_ACTION[result.attackType];
  const defenseNoun = result.attackType === 'AMBUSH' ? "the ambushed force's defenses" : "the enemy's defenses";

  return `${opener} ${defenseNoun} ${action}.`;
}
